// ============================================================
// Pay Rocky - RatesContext
// Server-backed live rates and admin rate updates.
// ============================================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { DEFAULT_RATES, USD_TO_NGN } from '../data/constants';
import { apiRequest, getStoredToken } from '../utils/api';
import { getIdempotencyKey, releaseIdempotencyKey, shouldRetainIdempotencyKey } from '../utils/idempotency';
import { readCachedValue, readStorageJSON, writeCachedValue } from '../utils/storage';
import { useAuth } from './AuthContext';

const RatesContext = createContext(null);
const PUBLIC_RATES_CACHE_KEY = 'payrocky_rates_public';
const ADMIN_RATES_CACHE_KEY = 'payrocky_rates_admin';
const AUTH_USER_CACHE_KEY = 'payrocky_auth_user';
const RATES_CACHE_TTL_MS = 60 * 1000;
const RATES_REFRESH_INTERVAL_MS = 60 * 1000;
const ADMIN_RATES_REQUEST_TIMEOUT_MS = 18000;
const ADMIN_RATE_UPDATE_TIMEOUT_MS = 45000;

function isAuthRoute() {
  if (typeof window === 'undefined') return false;
  const pathname = String(window.location.pathname || '').toLowerCase();
  return pathname === '/login'
    || pathname === '/signup'
    || pathname === '/forgot-password'
    || pathname.startsWith('/reset-password/');
}

function isStoredAdminSession() {
  const token = getStoredToken();
  if (!token) return false;

  const cachedAuth = readStorageJSON(AUTH_USER_CACHE_KEY, null);
  const role = cachedAuth?.value?.role || cachedAuth?.role || '';
  return String(role).toLowerCase() === 'admin';
}

function getRatesCacheKey() {
  return isStoredAdminSession() ? ADMIN_RATES_CACHE_KEY : PUBLIC_RATES_CACHE_KEY;
}

function readInitialRates() {
  const preferredCacheKey = getRatesCacheKey();
  const preferredRates = readCachedValue(preferredCacheKey, RATES_CACHE_TTL_MS, null);
  if (preferredRates) return preferredRates;

  const preferredStaleRates = readStaleRates(preferredCacheKey);
  if (preferredStaleRates) return preferredStaleRates;

  const fallbackCacheKey = preferredCacheKey === ADMIN_RATES_CACHE_KEY
    ? PUBLIC_RATES_CACHE_KEY
    : ADMIN_RATES_CACHE_KEY;

  const fallbackRates = readCachedValue(fallbackCacheKey, RATES_CACHE_TTL_MS, null);
  if (fallbackRates) return fallbackRates;

  const fallbackStaleRates = readStaleRates(fallbackCacheKey);
  if (fallbackStaleRates) return fallbackStaleRates;

  return [];
}

function readStaleRates(cacheKey) {
  const cached = readStorageJSON(cacheKey, null);
  if (!cached || typeof cached !== 'object') return null;
  return cached.value && typeof cached.value === 'object' ? cached.value : null;
}

function normalizeRates(ratesList = []) {
  const next = Object.entries(DEFAULT_RATES).reduce((acc, [cryptoId, fallbackRate]) => {
    acc[cryptoId] = {
      buy: Number(fallbackRate.buy) || 0,
      sell: Number(fallbackRate.sell) || 0,
      usdPrice: Number(fallbackRate.usdPrice) || 0,
      networkFees: {
        ...((fallbackRate.networkFees && typeof fallbackRate.networkFees === 'object') ? fallbackRate.networkFees : {}),
      },
    };
    return acc;
  }, {});
  const safeRatesList = Array.isArray(ratesList)
    ? ratesList
    : ratesList && typeof ratesList === 'object'
      ? Object.entries(ratesList).map(([crypto, value]) => ({
          crypto,
          buy: value?.buy || 0,
          sell: value?.sell || 0,
          usdPrice: value?.usdPrice || 0,
          feeBTC: value?.networkFees?.BTC,
          feeTRC20: value?.networkFees?.TRC20,
          feeERC20: value?.networkFees?.ERC20,
          feeBEP20: value?.networkFees?.BEP20,
          feeSOL: value?.networkFees?.SOL,
          feeLTC: value?.networkFees?.LTC,
          feeTON: value?.networkFees?.TON,
          feeBASE: value?.networkFees?.BASE,
          feeSUI: value?.networkFees?.SUI,
        }))
      : [];

  safeRatesList.forEach((rate) => {
    if (!rate?.crypto) return;
    const cryptoId = String(rate.crypto || '').toLowerCase();
    const fallbackRate = DEFAULT_RATES[cryptoId] || {};
    const incomingNetworkFees = rate.networkFees && typeof rate.networkFees === 'object'
      ? rate.networkFees
      : {};
    next[cryptoId] = {
      buy: Number(rate.buy) || 0,
      sell: Number(rate.sell) || 0,
      usdPrice: Number(rate.usdPrice) || Number(fallbackRate.usdPrice) || 0,
      networkFees: {
        ...((fallbackRate.networkFees && typeof fallbackRate.networkFees === 'object') ? fallbackRate.networkFees : {}),
        ...incomingNetworkFees,
        ...(typeof rate.feeBTC === 'number' ? { BTC: rate.feeBTC } : {}),
        ...(typeof rate.feeTRC20 === 'number' ? { TRC20: rate.feeTRC20 } : {}),
        ...(typeof rate.feeERC20 === 'number' ? { ERC20: rate.feeERC20 } : {}),
        ...(typeof rate.feeBEP20 === 'number' ? { BEP20: rate.feeBEP20 } : {}),
        ...(typeof rate.feeSOL === 'number' ? { SOL: rate.feeSOL } : {}),
        ...(typeof rate.feeLTC === 'number' ? { LTC: rate.feeLTC } : {}),
        ...(typeof rate.feeTON === 'number' ? { TON: rate.feeTON } : {}),
        ...(typeof rate.feeBASE === 'number' ? { BASE: rate.feeBASE } : {}),
        ...(typeof rate.feeSUI === 'number' ? { SUI: rate.feeSUI } : {}),
      },
    };
    });
  return next;
}

function normalizeSingleRate(rate) {
  if (!rate?.crypto) return null;
  return normalizeRates([rate])[rate.crypto] || null;
}

function normalizeRatePatch(ratesList = []) {
  const safeRatesList = Array.isArray(ratesList) ? ratesList : [ratesList];
  return safeRatesList.reduce((acc, rate) => {
    if (!rate?.crypto) return acc;
    const cryptoId = String(rate.crypto || '').toLowerCase();
    const normalizedRate = normalizeSingleRate(rate);
    if (normalizedRate) {
      acc[cryptoId] = normalizedRate;
    }
    return acc;
  }, {});
}

function canActivelyRefreshRates() {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

export function RatesProvider({ children }) {
  const { token, loading: authLoading } = useAuth();
  const [rates, setRates] = useState(() => normalizeRates(readInitialRates()));
  const [usdToNgn] = useState(USD_TO_NGN);
  const [lastUpdated, setLastUpdated] = useState(() => {
    const cached = readInitialRates();
    return cached ? new Date() : null;
  });
  const [fetching, setFetching] = useState(false);
  const [ratesError, setRatesError] = useState('');
  const ratesRef = useRef(rates);

  useEffect(() => {
    ratesRef.current = rates;
  }, [rates]);

  const fetchRates = useCallback(async (force = false) => {
    const cacheKey = getRatesCacheKey();
    if (!force) {
      const cachedRates = readCachedValue(cacheKey, RATES_CACHE_TTL_MS, null);
      if (cachedRates) {
        setRates(normalizeRates(cachedRates));
      }
    }

    setFetching(true);
    setRatesError('');
    try {
      const adminSession = cacheKey === ADMIN_RATES_CACHE_KEY;
      const res = await apiRequest(adminSession ? '/admin/rates' : '/rates', adminSession ? {
        token: getStoredToken(),
        timeoutMs: ADMIN_RATES_REQUEST_TIMEOUT_MS,
      } : {});
      const nextRates = normalizeRates(res.rates || []);
      setRates(nextRates);
      writeCachedValue(cacheKey, nextRates);
      setLastUpdated(new Date());
      return nextRates;
    } catch (err) {
      setRatesError(err.message || 'Failed to refresh rates.');
      console.warn('Rates fetch failed, using fallback rates:', err.message);
      const cachedRates = readCachedValue(cacheKey, RATES_CACHE_TTL_MS, null);
      if (cachedRates) {
        const nextRates = normalizeRates(cachedRates);
        setRates(nextRates);
        return nextRates;
      }
      const staleRates = readStaleRates(cacheKey) || readStaleRates(cacheKey === ADMIN_RATES_CACHE_KEY ? PUBLIC_RATES_CACHE_KEY : ADMIN_RATES_CACHE_KEY);
      if (staleRates) {
        const nextRates = normalizeRates(staleRates);
        setRates(nextRates);
        return nextRates;
      }
      return ratesRef.current;
    } finally {
      setFetching(false);
    }
  }, []);

  const adminRefreshRates = useCallback(async () => {
    setRatesError('');
    try {
      const res = await apiRequest('/admin/rates', {
        token: getStoredToken(),
        timeoutMs: ADMIN_RATES_REQUEST_TIMEOUT_MS,
      });
      const nextRates = normalizeRates(res.rates || []);
      setRates(nextRates);
      writeCachedValue(ADMIN_RATES_CACHE_KEY, nextRates);
      writeCachedValue(PUBLIC_RATES_CACHE_KEY, nextRates);
      setLastUpdated(new Date());
      return nextRates;
    } catch (error) {
      setRatesError(error.message || 'Failed to refresh rates.');
      throw error;
    }
  }, []);

  useEffect(() => {
    if (isAuthRoute()) return;
    if (authLoading && token) return;
    fetchRates();
  }, [authLoading, fetchRates, token]);

  useEffect(() => {
    if (isAuthRoute()) return undefined;
    if (authLoading && token) return undefined;

    const refreshIfVisible = () => {
      if (!canActivelyRefreshRates()) return;
      fetchRates(true).catch(() => {});
    };

    const intervalId = window.setInterval(refreshIfVisible, RATES_REFRESH_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfVisible();
      }
    };
    const handleWindowFocus = () => {
      refreshIfVisible();
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authLoading, fetchRates, token]);

  const adminUpdateRate = async (cryptoId, { buy, sell, networkFees, applyGlobally = false }) => {
    setRatesError('');
    if (!networkFees || typeof networkFees !== 'object') {
      throw new Error('Network fees must be an object');
    }
    const sanitizedNetworkFees = Object.entries(networkFees).reduce((acc, [network, value]) => {
      const normalizedNetwork = String(network || '').trim().toUpperCase();
      if (!normalizedNetwork) return acc;
      acc[normalizedNetwork] = Number(value) || 0;
      return acc;
    }, {});
    const idempotencyPayload = {
      crypto: cryptoId,
      buy: Number(buy),
      sell: Number(sell),
      applyGlobally: Boolean(applyGlobally),
      networkFees: sanitizedNetworkFees,
    };
    const idempotencyKey = getIdempotencyKey('admin:update-rates', idempotencyPayload);

    try {
      const res = await apiRequest('/admin/update-rates', {
        method: 'POST',
        token: getStoredToken(),
        timeoutMs: ADMIN_RATE_UPDATE_TIMEOUT_MS,
        idempotencyKey,
        body: {
          crypto: cryptoId,
          buy_rate: Number(buy),
          sell_rate: Number(sell),
          apply_globally: Boolean(applyGlobally),
          network_fees: sanitizedNetworkFees,
        },
      });
      releaseIdempotencyKey('admin:update-rates', idempotencyPayload);
      const payloadRates = Array.isArray(res.rates) && res.rates.length ? res.rates : [res.rate];
      const normalizedPayload = normalizeRatePatch(payloadRates);
      if (!Object.keys(normalizedPayload).length) {
        throw new Error('Rate update response was incomplete.');
      }
      setRates((prev) => {
        const nextRates = { ...prev };
        Object.entries(normalizedPayload).forEach(([id, rateEntry]) => {
          const fallbackUsdPrice = Number(prev[id]?.usdPrice)
            || Number(DEFAULT_RATES[id]?.usdPrice)
            || 0;
          nextRates[id] = {
            ...rateEntry,
            usdPrice: rateEntry.usdPrice || fallbackUsdPrice,
          };
        });
        writeCachedValue(ADMIN_RATES_CACHE_KEY, nextRates);
        writeCachedValue(PUBLIC_RATES_CACHE_KEY, nextRates);
        return nextRates;
      });
      setLastUpdated(new Date());
      return {
        appliedGlobally: Boolean(res.appliedGlobally),
        rates: normalizedPayload,
      };
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('admin:update-rates', idempotencyPayload);
      }
      throw error;
    }
  };

  const getBuyFeeUsd = (cryptoId, network) => {
    const rate = rates[cryptoId];
    if (!rate) return 0;
    return Number(rate.networkFees?.[network] || 0);
  };

  const calcNaira = (cryptoId, cryptoAmount, type = 'buy') => {
    const rate = rates[cryptoId];
    if (!rate) return 0;
    return cryptoAmount * rate[type];
  };

  const calcCrypto = (cryptoId, nairaAmount, type = 'buy') => {
    const rate = rates[cryptoId];
    if (!rate || rate[type] === 0) return 0;
    return nairaAmount / rate[type];
  };

  return (
    <RatesContext.Provider value={{
      rates,
      usdToNgn,
      lastUpdated,
      fetching,
      ratesError,
      fetchRates,
      adminRefreshRates,
      adminUpdateRate,
      getBuyFeeUsd,
      calcNaira,
      calcCrypto,
    }}>
      {children}
    </RatesContext.Provider>
  );
}

export function useRates() {
  const ctx = useContext(RatesContext);
  if (!ctx) throw new Error('useRates must be used inside <RatesProvider>');
  return ctx;
}
